import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  LoadSoulfileProjects,
  SetSoulfileProjects,
  CreateSoulfileProject,
  CreateSoulfileProjectWithOverride,
  DeleteSoulfileProject,
  SetSoulfileError,
  LoadCurrentSoulfileProject,
  UpdateCurrentSoulfileProject,
  SetCurrentSoulfileProject,
  LoadCurrentSoulfiles,
  SetCurrentSoulfiles,
  LoadCurrentSoulfile,
  SetCurrentSoulfile,
  CreateSoulfile,
  GoToCurrentSoulfileProject,
  LoadSoulfileProjectsCsv,
  SetSoulfileProjectsCsv,
  SetSoulfileCsvError,
} from './soulfile.actions';
import { SoulfileService } from './soulfile.service';
import { SoulfileProject } from 'src/app/admin/models/soulfile-project.model';
import { Soulfile } from 'src/app/admin/models/soulfile.model';
import { Router } from '@angular/router';
import { DownLoadFileService } from 'src/app/shared/services/download-file.service';

@Injectable()
export class SoulfileEffects {
  loadSoulfileProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSoulfileProjects),
      mergeMap(() =>
        this.soulfileService.loadProjects().pipe(
          map((projects: SoulfileProject[]) => SetSoulfileProjects({ projects })),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  loadSoulfileProjectsCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSoulfileProjectsCsv),
      mergeMap(() =>
        this.soulfileService.loadProjectsCsv().pipe(
          map((blob: Blob) => {
            this.downloadFileService.blobToFile(
              blob,
              'application/octet-stream',
              `soulfile-project-summaries-${new Date().toISOString()}.csv`,
            );
            return SetSoulfileProjectsCsv();
          }),
          catchError((error) => of(SetSoulfileCsvError({ error }))),
        ),
      ),
    ),
  );

  getSoulfileProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCurrentSoulfileProject),
      mergeMap(({ id }) =>
        this.soulfileService.getSoulfileProject(id).pipe(
          map((project: SoulfileProject) => SetCurrentSoulfileProject({ project })),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  createSoulfileProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSoulfileProject),
      mergeMap(({ avatarName }) =>
        this.soulfileService.createProject(avatarName).pipe(
          map(() => LoadSoulfileProjects()),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  createSoulfileProjectWithOverride$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSoulfileProjectWithOverride),
      mergeMap(({ avatarName, override }) =>
        this.soulfileService.createProjectWithOverride(avatarName, override).pipe(
          map(() => LoadSoulfileProjects()),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  updateSoulfileProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateCurrentSoulfileProject),
      mergeMap(({ id, project }) =>
        this.soulfileService.updateProject(id, project).pipe(
          map(() => LoadCurrentSoulfileProject({ id })),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  deleteSoulfileProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteSoulfileProject),
      mergeMap(({ id }) =>
        this.soulfileService.deleteProject(id).pipe(
          map(() => LoadSoulfileProjects()),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  loadSoulfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCurrentSoulfiles),
      mergeMap(({ projectId }) =>
        this.soulfileService.loadSoulfiles(projectId).pipe(
          map((soulfiles: Soulfile[]) => SetCurrentSoulfiles({ soulfiles })),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  loadSoulfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCurrentSoulfile),
      mergeMap(({ soulfileId }) =>
        this.soulfileService.loadSoulfile(soulfileId).pipe(
          map((soulfile: Soulfile) => SetCurrentSoulfile({ soulfile })),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  createSoulfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSoulfile),
      mergeMap(({ projectId, values, description }) =>
        this.soulfileService.createSoulfile(projectId, description, values).pipe(
          map(() => GoToCurrentSoulfileProject({ projectId })),
          catchError((error) => of(SetSoulfileError({ error }))),
        ),
      ),
    ),
  );

  goToCurrentSoulfileProject$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoToCurrentSoulfileProject),
        tap(({ projectId }) => this.router.navigate(['/admin', 'soulfile-projects', projectId])),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private soulfileService: SoulfileService,
    private router: Router,
    private downloadFileService: DownLoadFileService,
  ) {}
}
